import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const InterviewSocketContext = createContext(null);
const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 15000];

function buildSocketUrl(roomId) {
  const explicitBase = process.env.REACT_APP_EXTERNALINTERVIEW_WS_URL;
  const fallbackWsRoot = process.env.REACT_APP_WEBSOCKET_URL;

  if (explicitBase) {
    const explicitLooksLocalhost =
      explicitBase.includes("localhost") || explicitBase.includes("127.0.0.1");
    const browserIsLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    if (!(explicitLooksLocalhost && !browserIsLocalhost)) {
      return `${explicitBase.replace(/\/$/, "")}/${roomId}/`;
    }
  }

  if (fallbackWsRoot) {
    const base = fallbackWsRoot.replace(/\/$/, "");
    return `${base}/ws/externalinterview/${roomId}/`;
  }

  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const host = process.env.REACT_APP_EXTERNALINTERVIEW_WS_HOST || window.location.host;
  return `${protocol}://${host}/ws/externalinterview/${roomId}/`;
}

function normalizeParticipantIdentity(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeParticipantUserType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "staff") {
    return "interviewer";
  }
  return normalized;
}

function getOrCreateClientSessionId(roomId, userType, email) {
  const normalizedRoomId = String(roomId || "").trim();
  const normalizedUserType = normalizeParticipantUserType(userType);
  const normalizedEmail = normalizeParticipantIdentity(email);
  const key = `externalInterviewSession:${normalizedRoomId}:${normalizedUserType}:${normalizedEmail}`;
  let sessionId = localStorage.getItem(key);
  if (!sessionId) {
    sessionId =
      window.crypto?.randomUUID?.() ||
      `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, sessionId);
  }
  return sessionId;
}

export function SocketProvider({ roomId, email, userType, children }) {
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const outboundQueueRef = useRef([]);
  const listenersRef = useRef(new Map());
  const lastJoinRetryAtRef = useRef(0);
  const lastImmediateSyncAtRef = useRef(0);
  const lastRoomVersionRef = useRef(null);

  const [roomState, setRoomState] = useState(null);
  const [socketId, setSocketId] = useState(null);
  const [connectionState, setConnectionState] = useState("connecting");
  const normalizedEmail = useMemo(
    () => normalizeParticipantIdentity(email),
    [email]
  );
  const normalizedUserType = useMemo(
    () => normalizeParticipantUserType(userType),
    [userType]
  );

  const roleLabel = useMemo(
    () => (normalizedUserType || "unknown").toUpperCase(),
    [normalizedUserType]
  );

  const log = useCallback(
    (eventLabel, payload) => {
      if (payload === undefined) {
        console.log(`[${roleLabel}][${eventLabel}]`);
        return;
      }
      console.log(`[${roleLabel}][${eventLabel}]`, payload);
    },
    [roleLabel]
  );

  const dispatchEvent = useCallback((eventName, data) => {
    const handlers = listenersRef.current.get(eventName);
    if (!handlers || handlers.size === 0) {
      return;
    }

    handlers.forEach((handler) => {
      handler(data);
    });
  }, []);

  const sendRawPayload = useCallback((payload) => {
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      outboundQueueRef.current.push(payload);
      return;
    }
    socket.send(JSON.stringify(payload));
  }, []);

  const sendEvent = useCallback(
    (eventName, data = {}) => {
      const payload = {
        event: eventName,
        data,
      };

      if (eventName === "room:join") {
        log("ROOM_JOIN_SENT", payload);
      } else if (eventName === "webrtc:offer") {
        log("WEBRTC_OFFER_SENT");
      } else if (eventName === "webrtc:ice") {
        log("ICE_CANDIDATE_SENT");
      }

      sendRawPayload(payload);
    },
    [log, sendRawPayload]
  );

  const requestImmediateRoomSync = useCallback(
    (reason) => {
      const now = Date.now();
      if (now - lastImmediateSyncAtRef.current < 1000) {
        return;
      }
      lastImmediateSyncAtRef.current = now;
      sendEvent("room:sync", { reason });
    },
    [sendEvent]
  );

  const buildJoinPayload = useCallback(() => {
    const clientSessionId = getOrCreateClientSessionId(
      roomId,
      normalizedUserType,
      normalizedEmail
    );
    return {
      room_id: roomId,
      email: normalizedEmail,
      user_id: normalizedEmail,
      user_type: normalizedUserType,
      client_session_id: clientSessionId,
    };
  }, [normalizedEmail, normalizedUserType, roomId]);

  const connect = useCallback(() => {
    if (!roomId || !normalizedEmail || !normalizedUserType) {
      return;
    }

    if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) {
      wsRef.current.close();
    }

    const wsUrl = buildSocketUrl(roomId);
    log("WS_CONNECTING_URL", wsUrl);
    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;
    lastRoomVersionRef.current = null;
    setConnectionState(reconnectAttemptRef.current > 0 ? "reconnecting" : "connecting");

    socket.onopen = () => {
      if (wsRef.current !== socket) {
        return;
      }
      setConnectionState("connected");
      reconnectAttemptRef.current = 0;
      lastRoomVersionRef.current = null;

      sendEvent("room:join", buildJoinPayload());
      requestImmediateRoomSync("socket_open");

      while (outboundQueueRef.current.length > 0) {
        const queuedPayload = outboundQueueRef.current.shift();
        sendRawPayload(queuedPayload);
      }
    };

    socket.onmessage = (message) => {
      if (wsRef.current !== socket) {
        return;
      }

      let envelope = null;
      try {
        envelope = JSON.parse(message.data);
      } catch (err) {
        log("SOCKET_PAYLOAD_PARSE_ERROR", err);
        return;
      }

      const { event, data } = envelope;
      if (!event) {
        return;
      }

      if (event === "socket:connected") {
        setSocketId(data?.socket_id || null);
        log("SOCKET_CONNECTED", data?.socket_id || null);
      }

      if (event === "room:state") {
        const incomingVersion =
          typeof data?.version === "number" ? data.version : null;
        const lastVersion = lastRoomVersionRef.current;
        if (
          incomingVersion !== null &&
          lastVersion !== null &&
          incomingVersion < lastVersion
        ) {
          log("ROOM_STATE_STALE_IGNORED", {
            incomingVersion,
            lastVersion,
            status: data?.status,
          });
          return;
        }

        if (incomingVersion !== null) {
          lastRoomVersionRef.current = incomingVersion;
        }

        setRoomState(data);
        log("ROOM_STATE_RECEIVED", {
          status: data?.status,
          version: data?.version,
          waiting_cycle: data?.waiting_cycle,
          waiting_started_at: data?.waiting_started_at,
          participant_count: Array.isArray(data?.participants)
            ? data.participants.length
            : 0,
          participants: (data?.participants || []).map((participant) => ({
            user_type: participant?.user_type,
            state: participant?.state,
            user_id: participant?.user_id || participant?.email || null,
          })),
        });
        if (data?.status === "waiting" && data?.waiting_started_at) {
          log("WAITING_STARTED", {
            waiting_started_at: data.waiting_started_at,
            waiting_cycle: data.waiting_cycle,
          });
        }
      }

      if (event === "user:joined") {
        log("USER_JOINED_EVENT", data);
        requestImmediateRoomSync("user_joined_event");
      }

      if (event === "user:left") {
        log("USER_LEFT_EVENT", data);
        requestImmediateRoomSync("user_left_event");
      }

      if (event === "user:reconnected") {
        log("RECONNECT_DETECTED", data);
        requestImmediateRoomSync("user_reconnected_event");
      }

      if (event === "webrtc:answer") {
        log("WEBRTC_ANSWER_RECEIVED");
      }

      if (event === "webrtc:ice") {
        log("ICE_CANDIDATE_RECEIVED", data?.candidate);
      }

      dispatchEvent(event, data);
    };

    socket.onclose = (closeEvent) => {
      if (wsRef.current !== socket) {
        return;
      }
      wsRef.current = null;
      if (closeEvent?.code === 4001) {
        shouldReconnectRef.current = false;
        setConnectionState("disconnected");
        log("SESSION_REPLACED", {
          code: closeEvent.code,
          reason: closeEvent.reason || "duplicate_session_replaced",
        });
        return;
      }

      if (!shouldReconnectRef.current) {
        setConnectionState("disconnected");
        return;
      }

      setConnectionState("reconnecting");
      log("RECONNECT_DETECTED");

      const attemptIndex = Math.min(
        reconnectAttemptRef.current,
        RECONNECT_DELAYS_MS.length - 1
      );
      const waitMs = RECONNECT_DELAYS_MS[attemptIndex];
      reconnectAttemptRef.current += 1;

      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, waitMs);
    };

    socket.onerror = (error) => {
      if (wsRef.current !== socket) {
        return;
      }
      log("SOCKET_ERROR", error);
    };
  }, [
    buildJoinPayload,
    dispatchEvent,
    log,
    normalizedEmail,
    normalizedUserType,
    requestImmediateRoomSync,
    roomId,
    sendEvent,
    sendRawPayload,
  ]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connect();

    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current && wsRef.current.readyState < WebSocket.CLOSING) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  useEffect(() => {
    if (connectionState !== "connected") {
      return undefined;
    }

    const interval = setInterval(() => {
      sendEvent("room:sync", {});
    }, 5000);

    return () => clearInterval(interval);
  }, [connectionState, sendEvent]);

  useEffect(() => {
    if (connectionState !== "connected" || !roomState || roomState?.status === "ended") {
      return;
    }

    const participants = roomState?.participants || [];
    const selfParticipant =
      participants.find(
        (participant) =>
          participant?.user_type === normalizedUserType &&
          (normalizeParticipantIdentity(participant?.email) === normalizedEmail ||
            normalizeParticipantIdentity(participant?.user_id) === normalizedEmail)
      ) ||
      participants.find(
        (participant) => participant?.user_type === normalizedUserType
      );

    const selfConnected = selfParticipant?.state === "connected";
    if (selfConnected) {
      return;
    }

    const now = Date.now();
    if (now - lastJoinRetryAtRef.current < 2500) {
      return;
    }

    lastJoinRetryAtRef.current = now;
    log("ROOM_JOIN_RETRY", {
      state: selfParticipant?.state || "missing",
      status: roomState?.status,
    });
    sendEvent("room:join", buildJoinPayload());
  }, [
    buildJoinPayload,
    connectionState,
    log,
    normalizedEmail,
    normalizedUserType,
    roomState,
    sendEvent,
  ]);

  const subscribe = useCallback((eventName, handler) => {
    if (!listenersRef.current.has(eventName)) {
      listenersRef.current.set(eventName, new Set());
    }

    const bucket = listenersRef.current.get(eventName);
    bucket.add(handler);

    return () => {
      bucket.delete(handler);
      if (bucket.size === 0) {
        listenersRef.current.delete(eventName);
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      socketId,
      roomState,
      connectionState,
      sendEvent,
      subscribe,
      roleLabel,
    }),
    [connectionState, roleLabel, roomState, sendEvent, socketId, subscribe]
  );

  return (
    <InterviewSocketContext.Provider value={contextValue}>
      {children}
    </InterviewSocketContext.Provider>
  );
}

export function useInterviewSocket() {
  const context = useContext(InterviewSocketContext);
  if (!context) {
    throw new Error("useInterviewSocket must be used within ExternalInterview SocketProvider.");
  }
  return context;
}
